import React, { Component } from 'react';
import { withStyles, withTheme } from '@material-ui/core/styles';
import PropTypes from 'prop-types';

// modules
import * as d3 from 'd3';

// ui/ux
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

// ours
import Waiting from '../Waiting';
import { uniqueId, convertSnakeCase } from '../../common';

const styles = (theme) => ({
  bar: {
    fill: theme.palette.primary.main
  },
  active: {
    fill: theme.palette.secondary.main
  },
  svg: {
    '& text': {
      fill: theme.palette.getContrastText(theme.palette.background.paper),
      fontSize: '12px'
    },
    '& text.active': {
      fill: theme.palette.getContrastText(theme.palette.secondary.main)
    }
  }
});

class BarChart extends Component {
  constructor(props) {
    super(props);

    this.state = {
      id: uniqueId()
    };

    this.componentDidMount = this.componentDidUpdate;
  }

  componentDidUpdate() {
    const { classes } = this.props;
    const { id } = this.state;
    let svg = d3.select(`#${id}`).html('');

    let data = this.props.data;

    if (!data) return;

    const keys = Object.keys(data);

    const hashes = {};
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      const hash = k.trim().toLowerCase().hashCode();

      const existingIDX = hashes[hash];
      if (existingIDX !== undefined) {
        data[keys[existingIDX]] += data[k];
        delete data[k];
      } else {
        hashes[hash] = i;
      }
    }

    data = Object.keys(data)
      .map((k) => {
        return {
          k: convertSnakeCase(k),
          v: data[k]
        };
      })
      .sort((b, a) => {
        if (a.v === b.v) {
          return 0;
        }
        if (a.v < b.v) {
          return -1;
        } else {
          return 1;
        }
      });

    const margin = { top: 5, right: 5, bottom: 35, left: 15 },
      width = this.props.width - margin.left - margin.right,
      height = Math.max((this.props.width - margin.top - margin.bottom) / 1.77, 20 * data.length + margin.top + margin.bottom) - margin.top - margin.bottom;

    // set the ranges
    const y = d3.scaleBand().range([0, height]).padding(0.1);

    const x = d3.scaleLinear().range([0, width]);

    svg = svg
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

    // Scale the range of the data in the domains
    const maxX = d3.max(data.map((d) => d.v));
    y.domain(data.map((d) => d.k));
    x.domain([0, maxX]);

    const seventyFith = maxX * 0.75;

    // append the rectangles for the bar chart
    svg
      .selectAll('.bar')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', (d) => (d.v > seventyFith ? classes.active : classes.bar))
      .attr('y', (d) => {
        return y(d.k);
      })
      .attr('height', y.bandwidth())
      .attr('y', (d) => {
        return y(d.k);
      })
      .attr('width', (d) => {
        return x(d.v);
      });

    // add the x Axis
    svg
      .append('g')
      .attr('transform', 'translate(0,' + height + ')')
      .call(d3.axisBottom(x).tickFormat((d) => (~~d === d ? d : '')))
      .selectAll('text')
      .attr('y', 0)
      .attr('x', -10)
      .attr('dy', '.35em')
      .attr('transform', 'rotate(-90)')
      .style('text-anchor', 'end');

    // add the y Axis
    svg
      .append('g')
      .call(d3.axisLeft(y))
      .selectAll('text')
      .attr('x', 5)
      .style('text-anchor', 'start')
      .attr('class', (d, i) => {
        return data[i].v > seventyFith ? 'active' : null;
      });
  }

  render() {
    const { classes, data, width, title } = this.props;
    const { id } = this.state;

    if (!data) {
      return (
        <Box>
          <Typography variant="h6" gutterBottom>
            {convertSnakeCase(title)}
          </Typography>

          <Waiting height={width} />
        </Box>
      );
    }

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          {convertSnakeCase(title)}
        </Typography>

        <svg id={id} className={classes.svg} />
      </Box>
    );
  }
}

BarChart.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,

  data: PropTypes.object,
  title: PropTypes.string,

  width: PropTypes.number
};

BarChart.defaultProps = {
  width: 400
};

export default withTheme(withStyles(styles)(BarChart));
